/**
 * Workflow-name validation + auto-suffix helpers — frontend mirror.
 *
 * Same regex + suffix algorithm as:
 *   - backend/src/services/workflow-name.js (server-side check-name)
 *   - packages/cli/src/commands/workflows/name-helpers.js (CLI scaffold)
 *
 * Used by the marketplace deploy modal to:
 *   1. Validate shape instantly as the user types (no round-trip).
 *   2. Locally derive an auto-suffix when the project's existing
 *      workflow list (already loaded for the project context) shows
 *      the base name is taken.
 *
 * Server-side uniqueness via `GET /workflows/check-name` is still the
 * source of truth — between the user typing and submit, a teammate may
 * have deployed under the same name. The modal makes the check call
 * on submit; this helper just keeps the keystroke-level feedback fast.
 */

export const WORKFLOW_NAME_RE = /^[a-z][a-z0-9-]{0,62}[a-z0-9]$/;

export function validateWorkflowNameShape(name) {
  if (typeof name !== 'string' || name.length === 0) {
    return { valid: false, reason: 'Name is required', suggestion: null };
  }
  if (name.length < 2) {
    return { valid: false, reason: 'Name must be at least 2 characters', suggestion: null };
  }
  if (name.length > 64) {
    return { valid: false, reason: 'Name must be at most 64 characters', suggestion: null };
  }
  if (WORKFLOW_NAME_RE.test(name)) {
    return { valid: true, reason: null, suggestion: null };
  }
  const normalized = name
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  const suggestion = WORKFLOW_NAME_RE.test(normalized) ? normalized : null;
  return {
    valid: false,
    reason:
      'Use lowercase letters, digits, and hyphens. Start with a letter, end with a letter or digit.',
    suggestion,
  };
}

export function suggestAvailableName(base, taken) {
  const takenSet = taken instanceof Set ? taken : new Set(taken || []);
  if (!takenSet.has(base)) return base;
  const m = base.match(/^(.+)-(\d+)$/);
  const stem = m ? m[1] : base;
  for (let n = 2; n <= 99; n += 1) {
    const candidate = `${stem}-${n}`;
    if (WORKFLOW_NAME_RE.test(candidate) && !takenSet.has(candidate)) return candidate;
  }
  return null;
}

export function validateWorkflowName(name, existingNames = []) {
  const shape = validateWorkflowNameShape(name);
  if (!shape.valid) return shape;
  const takenSet = existingNames instanceof Set ? existingNames : new Set(existingNames || []);
  if (!takenSet.has(name)) {
    return { valid: true, reason: null, suggestion: null };
  }
  return {
    valid: false,
    reason: `'${name}' is already deployed in this project`,
    suggestion: suggestAvailableName(name, takenSet),
  };
}
